import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronDown, Plus, RefreshCw, Layers, Trash2, Loader2, Wallet, Moon, Sun, LogIn, X 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';

interface DemoTopBarProps {
  title?: string;
  selectedAccount?: any;
  accounts?: any[];
  onAccountChange?: (account: any) => void;
  onAddCex?: () => void;
  onAddDex?: () => void;
}

const DemoTopBar: React.FC<DemoTopBarProps> = ({ 
  title, 
  selectedAccount, 
  accounts = [], 
  onAccountChange, 
  onAddCex, 
  onAddDex 
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setConfirmDeleteId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getAccountColor = (account: any) => {
    if (account.id === 'all') return '#EF4444';
    if (account.type === 'dex') return '#A855F7';
    if (account.exchange === 'Binance') return '#F0B90B';
    if (account.exchange === 'Bybit') return '#F7A600';
    return '#666';
  };

  const getAccountIcon = (account: any) => {
    if (account.id === 'all') return <Layers size={18} className="text-red-500" />;
    if (account.type === 'dex') return <Wallet size={18} className="text-purple-400" />;
    return (
      <span className="text-xs font-black uppercase text-white">
        {account.name[0]}
      </span>
    );
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setConfirmDeleteId(id);
  };

  const handleDeleteConfirm = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
    // Locally simulate deletion for guest demo experience
    if (onAccountChange && selectedAccount?.id === id) {
      onAccountChange({ id: 'all', type: 'all', name: 'All Accounts' });
    }
  };

  const cexAccounts = accounts.filter(acc => acc.type === 'cex' || acc.id === 'binance' || acc.id === 'bybit');
  const dexAccounts = accounts.filter(acc => acc.type === 'dex');

  return (
    <div className="h-16 md:h-20 border-b border-neutral-900 flex items-center justify-between px-4 md:px-8 fixed md:sticky top-0 left-0 right-0 bg-black/90 backdrop-blur-md shrink-0 z-50 pt-[env(safe-area-inset-top)] md:pt-0">
      <div className="flex items-center gap-4 min-w-0 overflow-hidden flex-1">
        {title && (
          <h1 className="text-base md:text-xl font-bold tracking-tight text-white select-none pl-14 md:pl-0 truncate shrink-0">
            {title}
          </h1>
        )}
      </div>
      
      <div className="flex items-center gap-3 shrink-0 ml-2">
        {/* CexAccountSwitcher Mockup */}
        {selectedAccount && onAccountChange && (
          <div ref={dropdownRef} className="hidden md:block relative z-50 font-sans">
            
            {/* Trigger Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-3 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 rounded-lg px-4 py-2 transition-colors min-w-[200px] text-left cursor-pointer outline-none active:scale-[0.98]"
            >
              <div 
                className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${getAccountColor(selectedAccount)}20` }}
              >
                {getAccountIcon(selectedAccount)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{selectedAccount.name}</p>
                <p className="text-xs text-neutral-500 truncate">
                  {selectedAccount.id === 'all' ? 'Unified View' : selectedAccount.type === 'dex' ? 'Demo DEX' : 'Demo CEX'}
                </p>
              </div>
              <ChevronDown size={16} className={`text-neutral-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
              <div className="absolute top-full left-0 mt-2 bg-neutral-900 border border-neutral-800 rounded-lg shadow-xl z-[100] overflow-hidden min-w-[280px] animate-in fade-in slide-in-from-top-1 duration-200">
                
                {/* Header */}
                <div className="p-2 border-b border-neutral-800 flex items-center justify-between">
                  <span className="text-xs font-medium text-neutral-400">Accounts & Wallets (Demo)</span>
                  <button onClick={handleRefresh} className="p-1 hover:bg-neutral-800 rounded-md transition-colors cursor-pointer">
                    <RefreshCw size={14} className={`text-neutral-500 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {/* Scrollable List */}
                <div className="max-h-[360px] overflow-y-auto" data-lenis-prevent>
                  
                  {/* All Accounts */}
                  <button
                    onClick={() => {
                      onAccountChange({ id: 'all', type: 'all', name: 'All Accounts' });
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-800 transition-colors text-left ${selectedAccount.id === 'all' ? 'bg-neutral-800/50' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-md flex items-center justify-center bg-red-500/10 flex-shrink-0">
                      <Layers size={18} className="text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">All Accounts</p>
                      <p className="text-xs text-neutral-500">Unified View</p>
                    </div>
                    {selectedAccount.id === 'all' && <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                  </button>

                  {/* CEX Section */}
                  {cexAccounts.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 flex items-center gap-2">
                        <span className="text-[9px] font-bold text-neutral-600 uppercase tracking-widest">CEX Accounts</span>
                        <div className="flex-1 h-[1px] bg-neutral-800" />
                      </div>

                      {cexAccounts.map(account => {
                        const isSelected = selectedAccount.id === account.id;
                        if (account.id === 'all') return null;
                        return (
                          <div
                            key={account.id}
                            className={`group flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-800 transition-colors ${isSelected ? 'bg-neutral-800/50' : ''}`}
                          >
                            <button
                              onClick={() => {
                                onAccountChange(account);
                                setIsOpen(false);
                              }}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
                            >
                              <div 
                                className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${getAccountColor(account)}20` }}
                              >
                                {getAccountIcon(account)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{account.name}</p>
                                <p className="text-xs text-neutral-500">Active (Demo)</p>
                              </div>
                              {isSelected && <div className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />}
                            </button>

                            <div className="flex-shrink-0 flex items-center">
                              {confirmDeleteId === account.id ? (
                                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                  <span className="text-[10px] text-amber-400 font-medium whitespace-nowrap">Remove?</span>
                                  <button onClick={e => handleDeleteConfirm(e, account.id)} className="text-[10px] px-1.5 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded font-bold transition-colors">Yes</button>
                                  <button onClick={() => setConfirmDeleteId(null)} className="text-[10px] px-1.5 py-0.5 bg-neutral-700 hover:bg-neutral-600 text-white rounded font-bold transition-colors">No</button>
                                </div>
                              ) : (
                                <button onClick={e => handleDeleteClick(e, account.id)} className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/10 transition-all cursor-pointer">
                                  <Trash2 size={14} className="text-neutral-500 hover:text-red-400 transition-colors" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* DEX Section */}
                  {dexAccounts.length > 0 && (
                    <>
                      <div className="px-3 py-1.5 flex items-center gap-2">
                        <span className="text-[9px] font-bold text-neutral-600 uppercase tracking-widest">On-Chain Wallets</span>
                        <div className="flex-1 h-[1px] bg-neutral-800" />
                      </div>

                      {dexAccounts.map(account => {
                        const isSelected = selectedAccount.id === account.id;
                        return (
                          <div
                            key={account.id}
                            className={`group flex items-center gap-3 px-3 py-2.5 hover:bg-neutral-800 transition-colors ${isSelected ? 'bg-neutral-800/50' : ''}`}
                          >
                            <button
                              onClick={() => {
                                onAccountChange(account);
                                setIsOpen(false);
                              }}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
                            >
                              <div 
                                className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${getAccountColor(account)}20` }}
                              >
                                {getAccountIcon(account)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{account.name}</p>
                                <p className="text-xs text-neutral-500 font-mono truncate">{account.address || '0xDemoAddress...'}</p>
                              </div>
                              {isSelected && <div className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" />}
                            </button>

                            <div className="flex-shrink-0 flex items-center">
                              {confirmDeleteId === account.id ? (
                                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                  <span className="text-[10px] text-amber-400 font-medium whitespace-nowrap">Remove?</span>
                                  <button onClick={e => handleDeleteConfirm(e, account.id)} className="text-[10px] px-1.5 py-0.5 bg-red-600 hover:bg-red-500 text-white rounded font-bold transition-colors">Yes</button>
                                  <button onClick={() => setConfirmDeleteId(null)} className="text-[10px] px-1.5 py-0.5 bg-neutral-700 hover:bg-neutral-600 text-white rounded font-bold transition-colors">No</button>
                                </div>
                              ) : (
                                <button onClick={e => handleDeleteClick(e, account.id)} className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/10 transition-all cursor-pointer">
                                  <Trash2 size={14} className="text-neutral-500 hover:text-red-400 transition-colors" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>

                {/* Footer buttons */}
                <div className="border-t border-neutral-800 p-3 space-y-2">
                  <button
                    onClick={() => { setIsOpen(false); onAddCex?.(); }}
                    className="w-full flex items-center justify-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white py-2 rounded-lg transition-colors text-sm font-medium cursor-pointer"
                  >
                    <Plus size={16} />
                    Add CEX Account
                  </button>
                  <button
                    onClick={() => { setIsOpen(false); onAddDex?.(); }}
                    className="w-full flex items-center justify-center gap-2 bg-neutral-800/60 hover:bg-neutral-700 text-neutral-300 py-2 rounded-lg transition-colors text-sm font-medium border border-neutral-700 cursor-pointer"
                  >
                    <Wallet size={16} />
                    Add Wallet
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 md:gap-5">
          {/* Language switcher */}
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          
          {/* Dark/Light mode */}
          <button 
            onClick={toggleTheme}
            title={theme === 'dark' ? t('topbar.switchToLight') : t('topbar.switchToDark')}
            className="text-neutral-500 hover:text-white transition-colors cursor-pointer"
          >
            {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

          {/* Guest User / Login indicator */}
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-3 py-1.5 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 rounded-xl transition-all cursor-pointer text-xs font-bold text-neutral-300 hover:text-white"
          >
            <LogIn size={14} className="text-red-500" />
            <span>Login</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoTopBar;
